import { useState, useEffect } from 'react';
import GeckoPagination from 'services/gecko-pagination';
import moment from 'moment';
import {
  updateTagsFilters,
  resetTagsUsingConversationFilters,
} from '../../actions';
import { useSelector } from 'react-redux';

export const useTags = () => {
  const topTagsByConversation = useSelector(
    (state) => state.statsRoute.conversationTopTagsStats
  );

  const fetchingTopTagsStats = useSelector(
    (state) => state.statsRoute.fetchingTopTagsStats
  );

  const setToStartOfDay = (date) => date.startOf('day').toDate();
  const setToEndOfDay = (date) => date.endOf('day').toDate();
  const params = useSelector((state) => state.location.params);

  const {
    conversationPredefinedDateRange: predefinedDateRange,
    conversationCustomDateEnabled: customDateRangeEnabled,
    conversationStartDateFilter: startDate,
    conversationEndDateFilter: endDate,
    filteredAgentsAndTeams: agentsAndTeams,
    filteredBots: bots,
    filteredChannels: channels,
    filteredTags: tags,
    tagsShouldUseConversationFilters,
  } = useSelector((state) => state.statsRoute);

  const [filters, setFilters] = useState({
    startDate:
      (tagsShouldUseConversationFilters && startDate) ||
      setToStartOfDay(moment().subtract(7, 'days')),
    endDate:
      (tagsShouldUseConversationFilters && endDate) || setToEndOfDay(moment()),
    tags: (tagsShouldUseConversationFilters && tags) || [],
    bots: (tagsShouldUseConversationFilters && bots) || [],
    channels: (tagsShouldUseConversationFilters && channels) || [],
    agentsAndTeams: (tagsShouldUseConversationFilters && agentsAndTeams) || [],
    page: 1,
    sortKey: 'createdAt',
    sortDirection: 'DESC',
  });

  const maxCount = Math.max(...topTagsByConversation.map((item) => item.count));

  const topTagsWithPercentage = topTagsByConversation.map((item) => ({
    ...item,
    percentage: (item.count / maxCount) * 100,
  }));

  const [paginatedStats, pagination] = GeckoPagination.paginate({
    page: params.page,
    order_by: 'percentage',
    sort: 'DESC' || 'ASC',
  })(topTagsWithPercentage);

  const topTagsWithAiEmoji = paginatedStats.reduce((acc, item) => {
    const negative = Math.floor(parseFloat(item.negative));
    const neutral = Math.floor(parseFloat(item.neutral));
    const positive = Math.floor(parseFloat(item.positive));

    let general = '';
    let max = Math.max(negative, neutral, positive);

    if (max === negative) {
      general = `${Math.round(negative)}% Negative`;
    } else if (max === neutral) {
      general = `${Math.round(neutral)}% Neutral`;
    } else if (max === positive) {
      general = `${Math.round(positive)}% Positive`;
    }

    acc.push({
      name: `${item.ai_generated ? '✨ ' : ''}${item.name}`,
      count: parseInt(item.count),
      percentage: item.percentage,
      negative,
      neutral,
      positive,
      general,
    });

    return acc;
  }, []);

  useEffect(() => {
    updateTagsFilters(filters);
  }, [filters]);

  useEffect(() => {
    resetTagsUsingConversationFilters();
  }, []);

  const extraFilterProps = tagsShouldUseConversationFilters
    ? {
        predefinedDateRange,
        customDateRangeEnabled,
        selectedBots: filters.bots,
        selectedChannels: filters.channels,
        selectedAgents: filters.agentsAndTeams,
        selectedTags: filters.tags,
      }
    : {};

  const handleFiltersChange = ({
    startDate,
    endDate,
    filteredBots,
    filteredChannels,
    filteredAgents,
    filteredTags,
  }) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      startDate: setToStartOfDay(moment(startDate)),
      endDate: setToEndOfDay(moment(endDate)),
      bots: filteredBots,
      channels: filteredChannels,
      agentsAndTeams: filteredAgents,
      tags: filteredTags,
      page: 1,
    }));
  };

  return {
    fetchingTopTagsStats,
    filters,
    pagination,
    topTagsWithAiEmoji,
    extraFilterProps,
    handleFiltersChange,
  };
};

export default useTags;
